// src/components/auth/AccountSettings.js - Fixed tier display
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { USER_TIERS } from '../../services/UserService';
// eslint-disable-next-line no-unused-vars
import { getSubscriptionData } from '../../services/subscriptionService';
import PremiumUpgradeModal from '../PremiumUpgradeModal';
import SubscriptionStatus from '../SubscriptionStatus';
import PremiumFeatures from '../PremiumFeatures';

function AccountSettings({ isOpen, onClose }) {
  const { currentUser, logout, userTier, updateUserTier } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  
  useEffect(() => {
    // Initialize account settings component
    document.title = 'Account Settings - Pilates Workout Builder';
  }, []);
  
  if (!isOpen || !currentUser) return null;
  
  // Ensure we're showing at least BASIC tier for authenticated users
  const effectiveTier = (!userTier || userTier === USER_TIERS.NONE) 
    ? USER_TIERS.BASIC 
    : userTier;
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  const handleLogout = async () => {
    try {
      await logout();
      if (onClose) onClose();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };
  
  const handleOpenUpgradeModal = () => {
    setIsUpgradeModalOpen(true);
  };
  
  const handleSubscriptionSuccess = async (subscriptionData) => {
    setIsUpgradeModalOpen(false);
    
    // Update user tier to pro
    if (currentUser) {
      try {
        await updateUserTier(USER_TIERS.PRO);
      } catch (error) {
        console.error('Error updating user tier:', error);
      }
    }
  };
  
  const getTierDetails = () => {
    switch (effectiveTier) {
      case USER_TIERS.PRO:
        return {
          name: 'Premium',
          color: 'indigo',
          description: 'Full access to all features',
          features: [
            'Unlimited saved workouts',
            'Detailed analytics and progress tracking',
            'Access to all premium workout programs',
            'Multiple user profiles',
            'Priority support'
          ]
        };
      case USER_TIERS.BASIC:
        return {
          name: 'Basic',
          color: 'blue',
          description: 'Basic features for casual users',
          features: [
            'Save up to 5 workouts',
            'Basic progress tracking',
            'Save favorite exercises',
            'Create a single user profile'
          ]
        };
      default:
        return {
          name: 'Guest',
          color: 'gray',
          description: 'Limited access',
          features: [
            'Generate workouts',
            'Cannot save workouts',
            'No history tracking'
          ]
        };
    }
  };
  
  const tierInfo = getTierDetails();
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white p-4 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-800">Account Settings</h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="flex border-b">
          <button
            className={`py-3 px-4 border-b-2 font-medium text-sm ${
              activeTab === 'profile' 
                ? 'border-blue-500 text-blue-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            onClick={() => setActiveTab('profile')}
          >
            Profile
          </button>
          <button
            className={`py-3 px-4 border-b-2 font-medium text-sm ${
              activeTab === 'subscription' 
                ? 'border-blue-500 text-blue-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            onClick={() => setActiveTab('subscription')}
          >
            Subscription
          </button>
        </div>
        
        <div className="p-6">
          {activeTab === 'profile' && (
            <div>
              <div className="flex items-center mb-6">
                <div className="h-16 w-16 rounded-full bg-blue-500 text-white flex items-center justify-center text-2xl font-bold mr-4">
                  {currentUser.name?.charAt(0) || currentUser.email?.charAt(0) || 'U'}
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">{currentUser.name || 'User'}</h3>
                  <p className="text-gray-600">{currentUser.email}</p>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${tierInfo.color}-100 text-${tierInfo.color}-800 mt-1`}>
                    {tierInfo.name} Tier
                  </span>
                </div>
              </div>
              
              <div className="border-t border-gray-200 pt-4">
                <h4 className="font-medium text-gray-800 mb-2">Account Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-sm text-gray-500">Member Since</p>
                    <p className="font-medium">{formatDate(currentUser.createdAt)}</p>
                  </div>
                </div>
              </div>
              
              <div className="border-t border-gray-200 pt-4 mt-4">
                <h4 className="font-medium text-gray-800 mb-2">Account Actions</h4>
                <div className="space-y-2">
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 border border-red-300 text-red-600 hover:bg-red-50 rounded-md font-medium"
                  >
                    Log Out
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'subscription' && (
            <div>
              {/* Subscription Status Card */}
              <div className="mb-6">
                <SubscriptionStatus />
              </div>
              
              {/* Current Plan Features */}
              <div className={`bg-${tierInfo.color}-50 rounded-lg p-4 mb-6`}>
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className={`text-${tierInfo.color}-800 font-semibold text-lg`}>
                      Current Plan: {tierInfo.name}
                    </h3>
                    <p className={`text-${tierInfo.color}-700 mt-1`}>{tierInfo.description}</p>
                  </div>
                </div>
                
                <div className="mt-3">
                  <h4 className={`text-${tierInfo.color}-800 font-medium text-sm mb-2`}>Your Features:</h4>
                  <ul className="space-y-1">
                    {tierInfo.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <svg className={`h-5 w-5 text-${tierInfo.color}-500 mr-2`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className={`text-${tierInfo.color}-700 text-sm`}>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              {/* Premium Upgrade Option */}
              {effectiveTier === USER_TIERS.BASIC && (
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-medium text-gray-800">Upgrade to Pro</h3>
                    <button
                      onClick={handleOpenUpgradeModal}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
                    >
                      Upgrade Now
                    </button>
                  </div>
                  
                  <PremiumFeatures />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Premium Upgrade Modal */}
      <PremiumUpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        onSuccess={handleSubscriptionSuccess}
      />
    </div>
  );
}

export default AccountSettings;